import {
  queuedFetch,
  RequestPriority,
  cancelLowPriorityRequests,
  getQueueStats,
  isRequestCancelled,
} from '../requestQueue';
import { debugLogger, LogLevel, createLogger } from '../debugLogger';

const logger = createLogger('EnhancedImageService');

/**
 * Enhanced service for image requests with queue management and priorities
 */

interface ImageRequestOptions {
  priority?: RequestPriority;
  isRealTime?: boolean;
  isPreload?: boolean;
  // retryOnError removed - Retries are handled automatically in RequestQueueManager
}

class EnhancedImageService {
  private realTimeLoadComplete = false;

  private preloadInProgress = false;

  private lastDetectedMode: 'real-time' | 'replay' | 'unknown' = 'unknown';

  /**
   * Automatically detects if a request is real-time
   * based on the difference between the request time and current time
   */
  private detectRealTimeMode(timeParam: string): boolean {
    try {
      if (timeParam === 'unknown' || !timeParam) return false;

      const requestTime = new Date(timeParam).getTime();
      const currentTime = Date.now();
      const timeDifferenceMinutes = Math.abs(currentTime - requestTime) / (1000 * 60);

      // If the difference is less than 10 minutes, we consider it real-time
      const isRealTime = timeDifferenceMinutes <= 10;

      const currentMode = isRealTime ? 'real-time' : 'replay';

      // Log when mode changes
      if (this.lastDetectedMode !== 'unknown' && this.lastDetectedMode !== currentMode) {
        debugLogger.imageService(LogLevel.INFO, 'Mode change detected', {
          previousMode: this.lastDetectedMode,
          newMode: currentMode,
          requestTime: timeParam,
          currentTime: new Date(currentTime).toISOString(),
          timeDifferenceMinutes: Math.round(timeDifferenceMinutes * 100) / 100,
        });

        debugLogger.imageService(LogLevel.DEBUG, 'Mode change detected', {
          previousMode: this.lastDetectedMode,
          newMode: currentMode,
          requestTime: timeParam,
          timeDifferenceMinutes: Math.round(timeDifferenceMinutes * 100) / 100,
        });
      }

      this.lastDetectedMode = currentMode;

      return isRealTime;
    } catch (error) {
      debugLogger.imageService(LogLevel.WARN, 'Error detecting real-time mode', {
        timeParam,
        error: error instanceof Error ? error.message : 'Unknown error',
      });
      return false;
    }
  }

  /**
   * Gets an image with priority and queue management
   */
  async getImage(
    src: string,
    options: ImageRequestOptions = {},
  ): Promise<Response> {
    debugLogger.imageService(LogLevel.DEBUG, 'Image request initiated', {
      url: src.substring(0, 100) + (src.length > 100 ? '...' : ''),
      options,
      realTimeLoadComplete: this.realTimeLoadComplete,
      preloadInProgress: this.preloadInProgress,
    });

    // Extract time information from URL for debugging
    const urlParams = new URLSearchParams(src.split('?')[1] || '');
    const timeParam = urlParams.get('time') || urlParams.get('TIME') || 'unknown';

    // Automatically detect if it's real-time based on timestamp
    const isAutoDetectedRealTime = this.detectRealTimeMode(timeParam);

    // Use automatic detection if not manually specified
    const isRealTime = options.isRealTime !== undefined
      ? options.isRealTime
      : isAutoDetectedRealTime;
    const isPreload = options.isPreload || false;

    // Determine priority with the new logic
    const finalPriority = this.determinePriority({
      ...options,
      isRealTime,
      isPreload,
    });

    debugLogger.imageService(LogLevel.INFO, 'Priority determination completed', {
      time: timeParam,
      isRealTime,
      isPreload,
      isAutoDetectedRealTime,
      manualOverride: options.isRealTime !== undefined,
      originalPriority: EnhancedImageService.getPriorityName(options.priority || 'MEDIUM'),
      finalPriority: EnhancedImageService.getPriorityName(finalPriority),
      realTimeLoadComplete: this.realTimeLoadComplete,
      preloadInProgress: this.preloadInProgress,
    });

    // Si es real-time, remover peticiones de baja prioridad de la cola (sin cancelar activas)
    if (isRealTime) {
      debugLogger.imageService(LogLevel.INFO, 'Real-time request detected', {
        time: timeParam,
        detectionMethod: options.isRealTime !== undefined ? 'manual' : 'automatic',
        realTimeLoadComplete: this.realTimeLoadComplete,
        strategy: 'Non-disruptive priority insertion - clearing low priority queue only',
      });

      debugLogger.imageService(LogLevel.DEBUG, 'Real-time request detected, removing low priority from queue', {
        time: timeParam,
        detectionMethod: options.isRealTime !== undefined ? 'manual' : 'automatic',
        realTimeLoadComplete: this.realTimeLoadComplete,
        strategy: 'Non-disruptive - keep active requests running to avoid GeoServer resource waste',
      });
      cancelLowPriorityRequests();
    }

    debugLogger.imageService(LogLevel.DEBUG, 'Processing image request', {
      time: timeParam,
      isRealTime,
      isPreload,
      isAutoDetectedRealTime,
      finalPriority: EnhancedImageService.getPriorityName(finalPriority),
      realTimeLoadComplete: this.realTimeLoadComplete,
      preloadInProgress: this.preloadInProgress,
    });

    try {
      const response = await queuedFetch(
        src,
        {},
        finalPriority,
      );

      // Verify if the response is valid
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Mark that the first real-time load has been completed
      if (isRealTime && !this.realTimeLoadComplete) {
        this.realTimeLoadComplete = true;

        debugLogger.imageService(LogLevel.INFO, 'First real-time image loaded successfully', {
          time: timeParam,
          message: 'Preload can now proceed with normal priority',
          loadTime: Date.now(),
        });

        debugLogger.imageService(LogLevel.DEBUG, 'First real-time image loaded successfully', {
          time: timeParam,
          message: 'Preload can now proceed with normal priority',
        });
      }

      debugLogger.imageService(LogLevel.INFO, 'Image request completed successfully', {
        time: timeParam,
        isRealTime,
        isPreload,
        priority: EnhancedImageService.getPriorityName(finalPriority),
        status: response.status,
      });

      return response;
    } catch (error) {
      if (isRequestCancelled(error)) {
        // Dropped from the queue on purpose (real-time has priority): not an error.
        debugLogger.imageService(LogLevel.DEBUG, 'Image request cancelled', { time: timeParam });
        throw error;
      }
      debugLogger.imageService(LogLevel.ERROR, 'Image request failed', {
        time: timeParam,
        isRealTime,
        isPreload,
        priority: EnhancedImageService.getPriorityName(finalPriority),
        error: error instanceof Error ? error.message : String(error),
      });

      logger.error('Error loading image', {
        time: timeParam,
        isRealTime,
        isPreload,
        priority: EnhancedImageService.getPriorityName(finalPriority),
        error: error instanceof Error ? error.message : 'Unknown error',
        src: src.length > 100 ? `${src.substring(0, 100)}...` : src,
      });

      // Remove recursive retry logic to avoid infinite loops
      // Retries are handled in RequestQueueManager with appropriate limits
      throw error;
    }
  }

  /**
   * Helper to get readable priority name
   */
  private static getPriorityName(priority: RequestPriority): string {
    if (priority === 'HIGH') return 'HIGH';
    if (priority === 'MEDIUM') return 'MEDIUM';
    return 'LOW';
  }

  /**
   * Determines priority based on options
   */
  private determinePriority(options: ImageRequestOptions): RequestPriority {
    if (options.isRealTime) {
      return 'HIGH';
    }

    if (options.isPreload) {
      return this.realTimeLoadComplete ? 'MEDIUM' : 'LOW';
    }

    return 'MEDIUM';
  }

  /**
   * Determines if preload should be deferred
   */
  private shouldDeferPreload(isPreload: boolean): boolean {
    return isPreload && !this.realTimeLoadComplete;
  }

  /**
   * Starts preload mode
   */
  startPreloadMode(): void {
    this.preloadInProgress = true;
    debugLogger.imageService(LogLevel.DEBUG, 'Preload mode started', {
      realTimeLoadComplete: this.realTimeLoadComplete,
      preloadInProgress: this.preloadInProgress,
      queueStats: getQueueStats(),
    });
  }

  /**
   * Ends preload mode
   */
  endPreloadMode(): void {
    this.preloadInProgress = false;
    debugLogger.imageService(LogLevel.DEBUG, 'Preload mode ended', {
      realTimeLoadComplete: this.realTimeLoadComplete,
      preloadInProgress: this.preloadInProgress,
      queueStats: getQueueStats(),
    });
  }

  /**
   * Resets service state (useful when changing modes)
   */
  resetState(): void {
    const previousState = {
      realTimeLoadComplete: this.realTimeLoadComplete,
      preloadInProgress: this.preloadInProgress,
    };

    this.realTimeLoadComplete = false;
    this.preloadInProgress = false;
    // Note: clearAll not available in new queue implementation
    // requestQueue.clearAll();

    debugLogger.imageService(LogLevel.DEBUG, 'State reset', {
      previousState,
      newState: {
        realTimeLoadComplete: this.realTimeLoadComplete,
        preloadInProgress: this.preloadInProgress,
      },
      reason: 'Service state reset - likely mode change or restart',
    });
  }

  /**
   * Gets current service state
   */
  getServiceState() {
    return {
      realTimeLoadComplete: this.realTimeLoadComplete,
      preloadInProgress: this.preloadInProgress,
      queueStats: getQueueStats(),
    };
  }

  /**
   * Gets feature information
   */
  static async getFeatureInfo(
    src: string,
    priority: RequestPriority = 'MEDIUM',
  ): Promise<{value: number, unit: string}[]> {
    try {
      const response = await queuedFetch(src, {}, priority);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return data.features?.map((feature: any) => ({
        value: feature.properties.GRAY_INDEX,
        unit: feature.properties.unit,
      })) || [];
    } catch (error) {
      logger.error('Error fetching feature info', error);
      return [];
    }
  }

  /**
   * Gets feature information with elevation and coordinates
   */
  static async getFeatureInfoAndElevationAndCoordinates(params: {
    src: string;
    elevation: number;
    coordinates: number[];
    index: number;
  }): Promise<any[]> {
    try {
      const response = await queuedFetch(
        params.src,
        {},
        'MEDIUM',
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return data.features?.map((feature: any) => ({
        value: feature.properties.GRAY_INDEX,
        elevation: params.elevation,
        coordinates: params.coordinates,
        index: params.index,
      })) || [];
    } catch (error) {
      logger.error('Error fetching feature info with elevation', error);
      return [];
    }
  }
}

// Singleton para uso global
export const enhancedImageService = new EnhancedImageService();

export default EnhancedImageService;
